import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string | React.ReactNode;
  children?: React.ReactNode;
  error?: boolean;
  semanticId?: string;
}

/**
 * Fixed 18×18 hit target + decorations on this element (not on the input).
 * An input with opacity:0 hides input::before/::after in browsers — paint lives here instead.
 */
const ControlHitArea = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(18)};
  height: ${rem(18)};
  flex-shrink: 0;

  & > input[type='checkbox'] {
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    min-width: ${rem(18)};
    min-height: ${rem(18)};
    margin: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
  }

  &::before {
    box-sizing: border-box;
    position: absolute;
    content: '';
    width: ${rem(18)};
    height: ${rem(18)};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: ${rem(1)} solid var(--ui-text-subtle);
    border-radius: var(--ui-radius-sm, ${rem(4)});
    background-color: var(--ui-surface);
    pointer-events: none;
    z-index: 0;
  }

  &:has(> input:checked)::before {
    border: ${rem(1)} solid var(--ui-status-success);
  }

  &:has(> input:checked)::after {
    position: absolute;
    content: '\\f88b';
    color: var(--ui-text-inverse);
    font-size: ${rem(18)};
    background-color: var(--ui-status-success);
    width: ${rem(18)};
    height: ${rem(18)};
    border-radius: var(--ui-radius-sm, ${rem(4)});
    font-family: 'Material Symbols Outlined';
    font-weight: 900;
    line-height: ${rem(19)};
    display: flex;
    align-items: center;
    justify-content: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 0;
  }

  &:has(> input:indeterminate)::before {
    border: ${rem(1)} solid var(--ui-status-success);
  }

  &:has(> input:indeterminate)::after {
    position: absolute;
    content: '\\e15b';
    color: var(--ui-text-inverse);
    font-size: ${rem(18)};
    background-color: var(--ui-status-success);
    width: ${rem(18)};
    height: ${rem(18)};
    border-radius: var(--ui-radius-sm, ${rem(4)});
    font-family: 'Material Symbols Outlined';
    font-weight: 900;
    line-height: ${rem(19)};
    display: flex;
    align-items: center;
    justify-content: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 0;
  }

  &:has(> input:disabled)::before {
    background-color: var(--ui-background-muted);
    border: ${rem(1)} solid var(--ui-border);
    cursor: default;
  }

  &:has(> input:disabled):has(> input:checked)::after {
    background-color: var(--ui-text-subtle);
    cursor: default;
  }

  &:has(> input.is-invalid:not(:disabled))::before {
    border: ${rem(1)} solid var(--ui-status-danger);
  }
`;

/**
 * Wrapping the entire row in a <label> ensures clicking anywhere — the box
 * or the text — activates the input without relying on id/for pairing,
 * which can fail when ids contain dots (repeatable field paths).
 * The nested <span> replaces the inner <label> to keep valid HTML
 * (a <label> cannot contain another <label>).
 */
const Wrapper = styled.label`
  position: relative;
  display: flex;
  gap: ${rem(17)};
  min-width: ${rem(20)};
  align-items: center;
  min-height: ${rem(22)};
  cursor: pointer;

  &[data-disabled='true'] {
    cursor: default;
  }

  span.checkbox-label {
    margin: 0;
    font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
    font-size: var(--ui-checkbox-label-size, ${rem(16)});
    font-weight: 400;
    display: inline-block;
    line-height: var(--ui-checkbox-label-lh, ${rem(22)});
    align-self: center;
    color: var(--ui-text);
  }
`;

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, children, error = false, semanticId, id, name, disabled, className, ...rest },
  ref,
) {
  const autoId = React.useId();
  const semanticProps = getSemanticAttributes(semanticId);
  const inputId = id ?? name ?? autoId;
  const displayLabel = label ?? children;

  return (
    <Wrapper className={className} data-disabled={disabled ? 'true' : undefined} {...semanticProps}>
      <ControlHitArea>
        <input
          ref={ref}
          type="checkbox"
          id={inputId}
          name={name}
          disabled={disabled}
          data-ui-checkbox=""
          aria-invalid={error || undefined}
          className={error ? 'is-invalid' : ''}
          {...rest}
        />
      </ControlHitArea>
      {displayLabel != null && (
        <span className="checkbox-label">
          {displayLabel}
        </span>
      )}
    </Wrapper>
  );
});

Checkbox.displayName = 'Checkbox';
